// <pos-xiaonuan-reviewer/> — 小掌櫃今日推薦審核（內部代號小暖）
// see architecture.md#L410, src/lib/branding.js
// W4 Day 2 Step 2

import { listMarketingDrafts, actMarketingDraft } from '../lib/api.js';
import { BRAND_NAME } from '../lib/branding.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const STATUS_LABEL = {
  draft: { text: '待審', color: 'var(--brand-accent)' },
  approved: { text: '已批准', color: 'var(--success, #16a34a)' },
  rejected: { text: '已拒絕', color: 'var(--fg-muted)' },
  posted: { text: '已貼出', color: 'var(--brand-primary)' },
};

const URGENCY_LABEL = {
  expired: { text: '已過期', bg: '#dc2626' },
  high: { text: '緊急', bg: '#f97316' },
  medium: { text: '中等', bg: '#eab308' },
  low: { text: '一般', bg: '#94a3b8' },
};

class PosXiaonuanReviewer extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.drafts = [];
    this.loading = true;
    this.error = null;
    this.editingId = null;
    this.editText = '';
    this.busyIds = new Set();
    this.window = 'today';   // 'today' | '7d'
  }

  async connectedCallback() {
    this.render();
    await this.loadDrafts();
  }

  async loadDrafts() {
    this.loading = true;
    this.error = null;
    this.render();
    try {
      const opts = this.window === '7d' ? { days: 7 } : {};
      const res = await listMarketingDrafts(opts);
      this.drafts = res.drafts || [];
    } catch (err) {
      this.error = err?.message_zh || err?.message || '載入失敗';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async doAction(id, action, editedText) {
    if (this.busyIds.has(id)) return;
    this.busyIds.add(id);
    this.render();
    try {
      const payload = { id, action };
      if (action === 'edit_and_approve') payload.edited_copy_text = editedText;
      const res = await actMarketingDraft(payload);
      // local merge
      this.drafts = this.drafts.map((d) =>
        d.id === id ? { ...d, ...res.draft } : d
      );
      if (this.editingId === id) {
        this.editingId = null;
        this.editText = '';
      }
    } catch (err) {
      this.error = err?.message_zh || err?.message || '操作失敗';
    } finally {
      this.busyIds.delete(id);
      this.render();
    }
  }

  startEdit(draft) {
    this.editingId = draft.id;
    this.editText = draft.copy_text || '';
    this.render();
  }

  cancelEdit() {
    this.editingId = null;
    this.editText = '';
    this.render();
  }

  async copyToClipboard(text, btn) {
    try {
      await navigator.clipboard.writeText(text);
      const orig = btn.textContent;
      btn.textContent = '✓ 已複製';
      setTimeout(() => { btn.textContent = orig; }, 1500);
    } catch {
      btn.textContent = '⚠ 複製失敗';
    }
  }

  render() {
    const css = `
      :host { display: block; padding: var(--s-5); max-width: 880px; margin: 0 auto; }
      h2 { font-family: var(--font-display); color: var(--brand-primary); margin: 0 0 var(--s-2); }
      .subtitle { color: var(--fg-muted); font-size: 14px; margin-bottom: var(--s-4); }
      .toolbar { display: flex; gap: var(--s-2); margin-bottom: var(--s-4); }
      .toolbar button {
        padding: var(--s-2) var(--s-4); background: var(--bg-surface);
        color: var(--fg); border: 1px solid var(--border); border-radius: var(--r-md);
        cursor: pointer; font: inherit;
      }
      .toolbar button[aria-pressed="true"] { background: var(--brand-primary); color: white; border-color: var(--brand-primary); }
      .draft-card {
        background: var(--bg-elevated);
        border: 1px solid var(--border);
        border-radius: var(--r-lg);
        padding: var(--s-5);
        margin-bottom: var(--s-4);
      }
      .card-head {
        display: flex; justify-content: space-between; align-items: baseline;
        margin-bottom: var(--s-3); gap: var(--s-3); flex-wrap: wrap;
      }
      .target-date { font-size: 13px; color: var(--fg-muted); }
      .platform-tag {
        display: inline-block; padding: 2px 8px;
        background: var(--brand-accent, #ddd6fe); color: var(--fg-on-brand, #4338ca);
        border-radius: var(--r-sm); font-size: 11px; font-weight: 600;
      }
      .status-tag {
        display: inline-block; padding: 2px 10px; border-radius: 999px;
        font-size: 12px; font-weight: 600; color: white;
      }
      .reasoning {
        background: rgba(99, 102, 241, 0.06);
        border-left: 3px solid var(--brand-primary);
        padding: var(--s-3) var(--s-4); margin: var(--s-3) 0;
        border-radius: var(--r-sm); line-height: 1.6;
      }
      .reasoning .label { font-size: 12px; color: var(--brand-primary); font-weight: 600; margin-bottom: 4px; }
      .top-items { display: flex; gap: var(--s-2); flex-wrap: wrap; margin: var(--s-2) 0; }
      .item-chip {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 10px; border-radius: var(--r-md); background: var(--bg-surface);
        border: 1px solid var(--border); font-size: 13px;
      }
      .urgency-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
      .copy-text {
        white-space: pre-wrap; margin: var(--s-3) 0;
        padding: var(--s-4); background: var(--bg-surface);
        border: 1px solid var(--border); border-radius: var(--r-md);
        font-family: var(--font-display); line-height: 1.7; font-size: 15px;
      }
      .copy-text textarea {
        width: 100%; min-height: 120px; box-sizing: border-box;
        background: var(--bg); color: var(--fg);
        border: 1px solid var(--border); border-radius: var(--r-md);
        padding: var(--s-3); font: inherit; line-height: 1.7; resize: vertical;
      }
      details { margin-top: var(--s-2); }
      details summary { cursor: pointer; font-size: 13px; color: var(--fg-muted); }
      .image-prompt {
        margin-top: var(--s-2); padding: var(--s-3);
        background: var(--bg-surface); border: 1px dashed var(--border);
        border-radius: var(--r-md); font-family: var(--font-mono); font-size: 12px;
        white-space: pre-wrap;
      }
      .actions { display: flex; gap: var(--s-2); flex-wrap: wrap; margin-top: var(--s-4); }
      .actions button {
        padding: var(--s-2) var(--s-4); border-radius: var(--r-md); cursor: pointer;
        font: inherit; font-weight: 500; border: 1px solid transparent;
      }
      .actions .primary { background: var(--brand-primary); color: white; }
      .actions .ghost { background: transparent; color: var(--fg); border-color: var(--border); }
      .actions .danger { background: transparent; color: var(--danger, #dc2626); border-color: var(--danger, #dc2626); }
      .actions button:disabled { opacity: .5; cursor: not-allowed; }
      .empty, .loading, .err {
        text-align: center; padding: var(--s-10); color: var(--fg-muted); line-height: 1.8;
      }
      .err { color: var(--danger, #dc2626); }
      .meta { font-size: 12px; color: var(--fg-muted); margin-top: var(--s-2); }
    `;

    let body;
    if (this.loading) {
      body = `<div class="loading">載入中…</div>`;
    } else if (this.error && this.drafts.length === 0) {
      body = `<div class="err">⚠ ${escapeHtml(this.error)}</div>`;
    } else if (this.drafts.length === 0) {
      body = `<div class="empty">
        ${this.window === '7d' ? '近 7 天' : '今天'}還沒有推薦文案。<br/>
        <small>（每日 03:30 由 ${escapeHtml(BRAND_NAME)} 自動產生效期清庫存推薦）</small>
      </div>`;
    } else {
      body = this.drafts.map((d) => this.renderDraft(d)).join('');
    }

    this.shadowRoot.innerHTML = `
      <style>${css}</style>
      <h2>${escapeHtml(BRAND_NAME)}今日推薦</h2>
      <div class="subtitle">每日 03:30 自動掃效期 + 14 天銷售排行，產出清庫存文案。店長批准後可直接複製到 IG / FB / Threads。</div>

      <div class="toolbar">
        <button data-window="today" aria-pressed="${this.window === 'today'}">今天</button>
        <button data-window="7d" aria-pressed="${this.window === '7d'}">近 7 天</button>
        ${this.error ? `<span style="color:var(--danger,#dc2626);font-size:13px;align-self:center;">⚠ ${escapeHtml(this.error)}</span>` : ''}
      </div>

      ${body}
    `;

    this.shadowRoot.querySelectorAll('[data-window]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const w = btn.getAttribute('data-window');
        if (w !== this.window) {
          this.window = w;
          this.loadDrafts();
        }
      });
    });

    this.shadowRoot.querySelectorAll('[data-act]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.onActionClick(e));
    });

    this.shadowRoot.querySelectorAll('textarea[data-edit]').forEach((ta) => {
      ta.addEventListener('input', (e) => { this.editText = e.target.value; });
    });
  }

  renderDraft(d) {
    const status = STATUS_LABEL[d.status] || { text: d.status, color: 'gray' };
    const platform = (d.platform || 'generic').toUpperCase();
    const items = Array.isArray(d.top_items) ? d.top_items : [];
    const isEditing = this.editingId === d.id;
    const busy = this.busyIds.has(d.id);
    const text = isEditing ? this.editText : (d.copy_text || '');

    const itemChips = items.length === 0 ? '' : `
      <div class="top-items">
        ${items.map((i) => {
          const u = URGENCY_LABEL[i.urgency] || { text: i.urgency, bg: '#94a3b8' };
          return `
            <span class="item-chip">
              <span class="urgency-dot" style="background:${u.bg}"></span>
              ${escapeHtml(i.name || '(品項)')}
              <small style="color:var(--fg-muted)">· ${escapeHtml(u.text)}</small>
            </span>
          `;
        }).join('')}
      </div>
    `;

    let actionsHtml;
    if (isEditing) {
      actionsHtml = `
        <button class="primary" data-id="${d.id}" data-act="edit_and_approve" ${busy ? 'disabled' : ''}>儲存並批准</button>
        <button class="ghost" data-id="${d.id}" data-act="cancel_edit" ${busy ? 'disabled' : ''}>取消</button>
      `;
    } else if (d.status === 'draft') {
      actionsHtml = `
        <button class="primary" data-id="${d.id}" data-act="approve" ${busy ? 'disabled' : ''}>${busy ? '處理中…' : '批准'}</button>
        <button class="ghost" data-id="${d.id}" data-act="edit" ${busy ? 'disabled' : ''}>✏ 編輯</button>
        <button class="ghost" data-id="${d.id}" data-act="copy" data-text="${escapeHtml(text)}">📋 複製文案</button>
        <button class="danger" data-id="${d.id}" data-act="reject" ${busy ? 'disabled' : ''}>不採用</button>
      `;
    } else if (d.status === 'approved') {
      actionsHtml = `
        <button class="primary" data-id="${d.id}" data-act="mark_posted" ${busy ? 'disabled' : ''}>已貼出</button>
        <button class="ghost" data-id="${d.id}" data-act="copy" data-text="${escapeHtml(text)}">📋 複製文案</button>
      `;
    } else {
      actionsHtml = `
        <button class="ghost" data-id="${d.id}" data-act="copy" data-text="${escapeHtml(text)}">📋 複製文案</button>
      `;
    }

    return `
      <div class="draft-card">
        <div class="card-head">
          <div>
            <span class="status-tag" style="background:${status.color}">${escapeHtml(status.text)}</span>
            <span class="platform-tag">${escapeHtml(platform)}</span>
            <span class="target-date">· ${escapeHtml(d.target_date)}</span>
          </div>
        </div>

        ${d.owner_reasoning ? `
          <div class="reasoning">
            <div class="label">為什麼推薦</div>
            ${escapeHtml(d.owner_reasoning)}
          </div>
        ` : ''}

        ${itemChips}

        <div class="copy-text">
          ${isEditing
            ? `<textarea data-edit data-id="${d.id}" maxlength="2000">${escapeHtml(text)}</textarea>`
            : escapeHtml(text)}
        </div>

        ${d.image_prompt ? `
          <details>
            <summary>📷 配圖提示詞（可貼到 Midjourney / SD）</summary>
            <div class="image-prompt">${escapeHtml(d.image_prompt)}</div>
          </details>
        ` : ''}

        <div class="actions">${actionsHtml}</div>

        <div class="meta">
          ${d.approved_at ? `批准於 ${formatTs(d.approved_at)}` : ''}
          ${d.posted_at ? ` · 貼出於 ${formatTs(d.posted_at)}` : ''}
        </div>
      </div>
    `;
  }

  onActionClick(e) {
    const btn = e.currentTarget;
    const id = btn.getAttribute('data-id');
    const act = btn.getAttribute('data-act');
    const draft = this.drafts.find((d) => d.id === id);
    if (!draft) return;

    if (act === 'copy') {
      const text = btn.getAttribute('data-text') || '';
      this.copyToClipboard(text, btn);
      return;
    }
    if (act === 'edit') {
      this.startEdit(draft);
      return;
    }
    if (act === 'cancel_edit') {
      this.cancelEdit();
      return;
    }
    if (act === 'edit_and_approve') {
      const text = (this.editText || '').trim();
      if (!text) {
        this.error = '編輯內容不可空白';
        this.render();
        return;
      }
      this.doAction(id, 'edit_and_approve', text);
      return;
    }
    if (['approve', 'reject', 'mark_posted'].includes(act)) {
      this.doAction(id, act);
    }
  }
}

function formatTs(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('zh-TW', { hour12: false });
  } catch {
    return iso;
  }
}

customElements.define('pos-xiaonuan-reviewer', PosXiaonuanReviewer);
